import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Box, SxProps } from '@mui/material';

interface RightHandResizerContainerProps {
    activateResize?: boolean;
    centerContent: React.ReactNode;
    rightContent: React.ReactNode;
    drawerOpen?: boolean;
    initialWidth: number;
    onCenterWidthChange?: (width: { rightPanelWidth: number; rightPanelAndDrawerWidth: number }) => void;
    maxWidth?: number;
    minWidth?: number;
    sxProps?: SxProps;
}

const STORAGE_KEY = 'rightHandPanelWidth';

export default function RightHandResizerContainer({
    activateResize = true,
    drawerOpen = false,
    centerContent,
    rightContent,
    initialWidth,
    maxWidth = 574,
    minWidth = 382,
    onCenterWidthChange,
    sxProps
}: RightHandResizerContainerProps) {
    const drawerWidth = 240;
    const midWidth = 382;

    const sideBordersAndResizerBorderWidth = 48;

    const getInitialWidth = () => {
        if (initialWidth === -1) {
            return midWidth;
        }
        return initialWidth || minWidth;
    };

    const getStoredWidth = () => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const parsedWidth = parseInt(stored, 10);
                if (!isNaN(parsedWidth) && parsedWidth >= minWidth && parsedWidth <= maxWidth) {
                    return parsedWidth;
                }
            }
        } catch (error) {
            console.warn('Error reading rightHandPanelWidth from localStorage:', error);
        }
        return null;
    };

    const saveWidthToStorage = (width: number) => {
        try {
            localStorage.setItem(STORAGE_KEY, width.toString());
        } catch (error) {
            console.warn('Error saving rightHandPanelWidth to localStorage:', error);
        }
    };

    const minWidthRef = useRef(initialWidth === -1 ? midWidth : initialWidth);
    const maxWidthRef = useRef(maxWidth);

    const storedWidth = getStoredWidth();

    const initialWidthValue = storedWidth !== null ? storedWidth : getInitialWidth();

    const [rightHandPanelWidth, setRightHandPanelWidth] = useState(initialWidthValue);
    const isResizing = useRef(false);

    const handleMouseDown = () => {
        isResizing.current = true;
        document.body.style.cursor = 'col-resize';
    };

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!isResizing.current) return;
        const newWidth = window.innerWidth - e.clientX;
        if (newWidth >= minWidthRef.current && newWidth <= maxWidthRef.current) {
            setRightHandPanelWidth(newWidth);
            saveWidthToStorage(newWidth);
        }
    }, []);

    const handleMouseUp = () => {
        isResizing.current = false;
        document.body.style.cursor = 'default';
    };

    useEffect(() => {
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [handleMouseMove]);

    useEffect(() => {
        onCenterWidthChange?.({
            rightPanelWidth: rightHandPanelWidth + sideBordersAndResizerBorderWidth,
            rightPanelAndDrawerWidth: rightHandPanelWidth + sideBordersAndResizerBorderWidth + (drawerOpen ? drawerWidth : 0)
        });
    }, [drawerOpen, rightHandPanelWidth, activateResize, onCenterWidthChange]);

    useEffect(() => {
        maxWidthRef.current = maxWidth;

        if (initialWidth === -1) {
            minWidthRef.current = minWidth;
        } else {
            minWidthRef.current = initialWidth;
        }

        if (activateResize) {
            if (!isResizing.current) {
                const newWidth = storedWidth !== null ? storedWidth : midWidth;
                setRightHandPanelWidth(newWidth);
            }
        } else {
            setRightHandPanelWidth(initialWidth);
        }
    }, [initialWidth, rightHandPanelWidth, activateResize, storedWidth, midWidth, maxWidth, minWidth]);

    return (
        <Box id="right-hand-resizer-container" sx={{ display: 'flex', height: '100%', overflow: 'hidden', ...sxProps }}>
            <Box sx={{ flex: 1 }}>{centerContent}</Box>
            {activateResize && (
                <Box
                    sx={(theme) => ({
                        width: '8px',
                        backgroundColor: theme.palette.background.paper,
                        zIndex: 10,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    })}
                >
                    <Box
                        onMouseDown={handleMouseDown}
                        sx={(theme) => ({
                            width: 'calc(100% - 4px)',
                            height: '10%',
                            backgroundColor: theme.palette.primary.main,
                            borderRadius: '10px',
                            cursor: 'col-resize'
                        })}
                    />
                </Box>
            )}
            <Box
                sx={(theme) => ({
                    width: `${rightHandPanelWidth}px`,
                    minWidth: minWidthRef.current,
                    maxWidth: maxWidthRef.current,
                    bgcolor: theme.palette.background.paper,
                    borderLeft: '1px solid #ddd',
                    overflowY: 'hidden'
                })}
            >
                {rightContent}
            </Box>
        </Box>
    );
}
